import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Link } from '@zola/zola-ui/src/components/Link';
import { ChevronDownIcon } from '@zola/zola-ui/src/components/SvgIconsV3/ChevronDown';
import { ChevronRightIcon } from '@zola/zola-ui/src/components/SvgIconsV3/ChevronRight';

import './AccordionSection.less';

const CategoryPropTypes = PropTypes.shape({
  id: PropTypes.string,
  key: PropTypes.string,
  name: PropTypes.string.isRequired,

  // Its recrusive, this is the WCategoryTree[] type
  children: PropTypes.arrayOf(PropTypes.shape({})),
});

const AccordionTypes = {
  category: CategoryPropTypes,
  onLabelClick: PropTypes.func,
  activeKey: PropTypes.string,
  openSections: PropTypes.shape({}),
};

/**
 * Class representing an AccordionSection
 * @extends Component
 */
const AccordionList = ({ category, onLabelClick, activeKey, openSections }) => {
  const categoryKey = category.key || category.id;
  const isOpen = openSections[categoryKey];
  const hasChildren = category.children && category.children.length !== 0;

  return hasChildren ? (
    <AccordionSection
      key={categoryKey}
      category={category}
      onLabelClick={onLabelClick}
      activeKey={activeKey}
      openSections={openSections}
    />
  ) : (
    <li className="accordion-section__item" key={categoryKey}>
      <Link
        role="button"
        variant="primary"
        noUnderline
        subtle={!isOpen}
        className={classnames('accordion-section__link', { 'is-active is-bold': isOpen })}
        onClick={onLabelClick(category)}
      >
        {category.name}
      </Link>
    </li>
  );
};

AccordionList.propTypes = AccordionTypes;

export default function AccordionSection({ category, onLabelClick, activeKey, openSections }) {
  const { children, name } = category;
  const categoryKey = category.key || category.id;

  // render accordion if there is more than 1 subcategory (grandchild) for a category
  if (children && children.length <= 1) return null;

  const isOpen = openSections[categoryKey];
  const ChevronComponent = isOpen ? ChevronDownIcon : ChevronRightIcon;
  const titleClassName = classnames(
    'accordion-section__title',
    { 'is-active': categoryKey === activeKey },
    { 'is-bold': isOpen }
  );

  return (
    <div className="accordion-section">
      <ChevronComponent width={10} height={10} />
      <p onClick={onLabelClick(category)} className={titleClassName}>
        {name}
      </p>
      {isOpen && (
        <ul className="accordion-section__itemList">
          {children.map((child, index) => (
            <AccordionList
              key={index}
              category={child}
              onLabelClick={onLabelClick}
              activeKey={activeKey}
              openSections={openSections}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

AccordionSection.propTypes = AccordionTypes;
